<template>
  <section class="mx-4 py-2">
    <!-- Grade responsiva 2x2 (md+) / 1x4 (xs) -->
    <v-row>
      <chart-grid
          :active="analyticsProStore.activeFilter !== 'ALL' || anyChartFiltered"
          :charts-to-render="chartsToRender"
          @chart-mounted="handleRegisterChart"
          @resetCurrentChart="resetCurrentChart"

          />
      
      <!-- tabulado-->
      <v-col cols="12">
        <v-card class="d-flex flex-column chart-card" elevation="0">
          <the-data-table :dim="dim" :tableData="tableData" :ndx="ndx" chart-group="flowsight-conversions" />
        </v-card>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import crossfilter from "crossfilter2";
import * as dc from "dc";
import "dc/dist/style/dc.css";

import { useAnalyticsProStore } from "@/store/analyticsStore";
import { mapStores } from "pinia";
import TheDataTable from "./components/TheDataTable.vue";
import ChartGrid from "./components/ChartGrid.vue";
import { safeRenderAll, safeRedrawAll, createScheduledRedraw, createResizeWatcher } from "./runtime/chartLifecycle.js";
import { createDrilldownController } from "./runtime/drilldownController.js";
import { createConversionRateByHourChart } from "./conversions/createConversionRateByHourChart.js";
import { createConversionHeatmapChart } from "./conversions/createConversionHeatmapChart.js";
import { createVolumeBySideChart } from "./conversions/createVolumeBySideChart.js";
import { createConversionBubbleChart } from "./conversions/createConversionBubbleChart.js";
import { toDate } from "@/utils/flowSightPeriods";
import { VOLUME_LABELS, volumeLabelFromNotional } from "@/utils/flowSightBuckets";
import { HOUR_LABELS, hourIndexFromHour, hourLabelFromHour } from "@/utils/flowSightHourBuckets";
import { isNegotiated } from "./calculations/calculateNegotiatedVolumeBRL.js";
import { createConversionRateReducer, emptyConversionValue } from "./calculations/conversionRate.js";

const CHART_GROUP = "flowsight-conversions";

export default {
  components: {
    TheDataTable,
    ChartGrid
  },
  data() {
    return {
      chartsId:[],
      ndx: null,
      tableData: {},
      groupId: null,
     // Eixos fixos
     volBuckets: VOLUME_LABELS,
     charts: { bar1: null, heat2: null, bar3: null, bubble4: null },
     dim: {},
     grp: {},
     chartFilterCount: 0,
     // Generation of the dataset this page's crossfilter was built from.
     // -1 means "never built".
     builtGeneration: -1,
    isActive: false,
     _unsubStore: null,
   }
 },
 computed: {
   ...mapStores(useAnalyticsProStore),
   chartsToRender() {
     return [
       {
         title: this.$t('flowSightSpot.chart1'),
         id: "chart1",
       },
       {
         title: this.$t('flowSightConversions.chart2'),
         id: "chart2",
         // Colours here are a volume ramp, not the BUY/SELL palette.
         hideSideLegend: true,
       },
       {
         title: this.$t('flowSightSpot.chart3'),
         id: "chart3",
       },
       {
         title: this.$t('flowSightSpot.chart4'),
         id: "chart4",
       },
     ];
   },
     anyChartFiltered() {
    return this.chartFilterCount >0;
  },

 },
 watch: {
   // Fires on EVERY dataset load, whichever page triggered it. The early
   // return below can still swallow the rebuild if the chart hosts are not
   // mounted yet - activated() catches that by comparing generations.
   'analyticsProStore.datasetGeneration': {
     immediate: false,
     handler() {
       if (this.chartsId.length < 4) return
       this.$nextTick(() => this.initPipeline())
     }
   },
   'analyticsProStore.periodSelection': {
     immediate: false,
     deep: true,
     handler() {
       if (!this.isActive) return
       // Slider updates apply the global TENOR window immediately.
       this._applyGlobalTenorFromStore('TENOR')
     }
   },
      'analyticsProStore.sideFilter': {
     immediate: false,
     handler(arr) {
       if(!this.isActive) return
       this._applyGlobalSideFromStore(arr)
     },
   },
   'analyticsProStore.counterpartyFilter': {
     immediate: false,
     handler(names) {
       if(!this.isActive) return
       this._applyGlobalCounterpartyFromStore(names)
     },
   },
   'analyticsProStore.rfqPeriod': {
     immediate: false,
     handler() {
       if(!this.isActive) return
       this._applyRfqPeriodFromStore()
     },
   },
   'analyticsProStore.activeFilter': {
     immediate: false,
     handler(val) {
       this._applyGlobalTenorFromStore(val)
     }
   }
 },
 activated() {
      try {
    this.$nextTick(() => {
      // Stale generation means a dataset was loaded while this page was
      // hidden and the rebuild was skipped - `ndx` exists but describes the
      // previous file.
      if (!this.ndx
        || dc.chartRegistry.list(CHART_GROUP).length === 0
        || this.builtGeneration !== this.analyticsProStore.getDatasetGeneration) {
        this.initPipeline() // first mount, charts lost, or stale dataset
        return
      }
      this.onResize()
      this._applyGlobalSideFromStore(this.analyticsProStore.sideFilter)
      this._applyGlobalCounterpartyFromStore(this.analyticsProStore.counterpartyFilter)
      this._applyRfqPeriodFromStore()
      this._applyGlobalTenorFromStore(this.analyticsProStore.activeFilter)
      this.scheduleRedraw()
    })
    } catch (e) {
      console.warn("Error refreshing dc.js charts on activate", e);
    }
    this.isActive= true

 },
deactivated() {
  this.isActive = false;
  this.clearPageFilters()
},
 mounted () {
   this.groupId= this.analyticsProStore.CONVERSION_ID
   this._resizeWatcher = createResizeWatcher(this.onResize)
 },
 beforeDestroy () {
   this._resizeWatcher?.disconnect()
   try { dc.deregisterAllCharts(CHART_GROUP) } catch (_) {}
  },

  methods: {
    handleRegisterChart(e)
    {
      this.chartsId.push(e.id)
      this._resizeWatcher?.observe(e.id)
    },
   // ===== Helpers =====
   // Single entry point for this tab's data - the four charts AND the table all
   // read what this returns, so the conversion universe is enforced here once
   // instead of in each reducer.
   //
   // isNegotiated = DEAL or NOTH.DONE: the RFQs where the client decided on a
   // price we actually showed. REJECTED / EXP.QUOTE / QUOTE / NEW.ORDER / blank
   // never got there, so they belong in neither the numerator nor the
   // DENOMINATOR of a conversion rate. Same predicate the Overview "Taxa de
   // Conversão" KPI uses, so the two tabs finally agree on the population.
   _getStoreRows () {
     const rows = (typeof this.analyticsProStore.getAnalytics === 'function')
       ? this.analyticsProStore.getAnalytics()
       : this.analyticsProStore.analytics
     return Array.isArray(rows) ? rows.filter(isNegotiated) : []
   },
   getW (el) {
     return Math.max(200, (el?.clientWidth) || 600)
   },
   // Base height every chart on the tab starts from; each builder then adds its
   // own offset for axis/label room (see each createXxx).
   getH () {
     return 290
   },


   // Setting .width() is not enough for a coordinate-grid chart. On redraw dc
   // only re-renders the X axis when `elasticX || _resizing || render`
   // (dc.js:4576), and `_resizing` is raised by rescale(), which _prepareXAxis
   // calls only when the DOMAIN changed. The bubble chart has a fixed domain
   // (one slot per maturity band) and elasticX off, so neither condition ever
   // fires: plotData would move the bubbles to the new width while the axis
   // kept drawing its ticks at the old pixel positions. Telling dc it is
   // resizing is what re-renders the axis.
   _setChartWidth (chart, el) {
     if (!chart || typeof chart.width !== 'function') return
     chart.width(this.getW(el))
     if (typeof chart.rescale === 'function') chart.rescale()
   },
   onResize () {
     if (!this.isActive) return
     this._setChartWidth(this.charts.bar1, this.chartsId[0])
     this._setChartWidth(this.charts.heat2, this.chartsId[1])
     if (this.charts.bar3 && typeof this.charts.bar3.redraw === 'function') this.charts.bar3.redraw()
     this._setChartWidth(this.charts.bubble4, this.chartsId[3])
     safeRedrawAll(CHART_GROUP)
   },

   renderAll () {
     try {
       safeRenderAll(CHART_GROUP)
       this.$nextTick(() => safeRedrawAll(CHART_GROUP))
     } catch (_) {}
   },


   // ===== Pipeline =====
   initPipeline () {
    if (this.chartsId.length < 4) return;
    // No layout means the page is deactivated by keep-alive and every
    // clientWidth reads 0, so the charts would be built at getW()'s fallback.
    // Leave builtGeneration stale and let activated() rebuild once measurable.
    if (!(this.chartsId[0]?.clientWidth > 0)) return;

     const rawRows = this._getStoreRows()
    //  if (!rawRows || rawRows.length === 0) {
    //    try { dc.deregisterAllCharts() } catch (_) {}
    //    return
    //  }
    //   const data = rawRows.map(this.analyticsProStore.normalizeRow)
    //   
    this.tableData = {rows: rawRows}

     Object.values(this.charts).forEach((ch) => {
       if (!ch) return
       try { ch.filterAll?.() } catch (_) {}
       try { dc.deregisterChart(ch, CHART_GROUP) } catch (_) {}
     })
     this.builtGeneration = this.analyticsProStore.getDatasetGeneration
     this.ndx = crossfilter(rawRows)
     this.count = this.ndx.groupAll().value()

     this.buildDimensionsAndGroups()
     this.buildBarChart()
     this.buildHeatmap()
     this.buildBarSplitChart()
     this.buildBubbleChart()


     dc.chartRegistry.list(CHART_GROUP).forEach(ch => {
        if (!ch || typeof ch.on !== 'function') return
        ch.on('filtered.updateCount', () => {
          this.count = this.ndx.groupAll().value()
        })
        ch.on('filtered.globalFlag', () => this.syncFilterFlag())
      })


     this._applyGlobalSideFromStore(this.analyticsProStore.sideFilter)
     this._applyGlobalCounterpartyFromStore(this.analyticsProStore.counterpartyFilter)
     this._applyRfqPeriodFromStore()
     this._applyGlobalTenorFromStore(this.analyticsProStore.activeFilter)

     this.renderAll()
   },


   // ===== Normalização =====
   /*
   normalizeRow (row) {
     // Datas
     const rfqTs = this.parseDateSmart(
       this.pick(row, ['rfqtimestamp','rfqTimestamp','date','Date']),
       this.pick(row, ['Timestamp'])
     )
     const matTs = this.parseDateSmart(
       this.pick(row, ['maturitydate','maturityDate','Maturity date'])
     )


     // Tenor (em dias)
     const msInDay = 86400000
     const tenorDays = (rfqTs && matTs) ? Math.max(0, Math.round((matTs - rfqTs) / msInDay)) : 0
     const tenorIndex = this.tenorIndexFromDays(tenorDays)
     const tenor      = tenorLabelFromIndex(tenorIndex)


     // Notional / buckets
     const notional = this.toNumSmart(this.pick(row, ['notional','amount','Amount']))
     const volBucket = this.bucketizeVol(notional)


     // Side e status/conversão
     const sideRaw = this.pick(row, ['side','Side','RFQ side','Side (Blotter)'])
     const side = this.normalizeSide(sideRaw)
     const status = this.deriveStatus(row)
     const convFlag = (String(status).toUpperCase() === 'DEAL') ? 1 : 0


     // Números com vírgula do report
     const toN = (names) => this.toNumSmart(this.pick(row, names))


     return {
       id: this.pick(row, ['id','Id','ID']),
       counterparty: this.pick(row, ['counterparty','Counterparty']),
       cnpj: this.pick(row, ['cnpj','CNPJ']),
       currency: this.pick(row, ['currency','Currency','Currency pair']),
       parity: this.pick(row, ['parity','Parity','Currency pair']),


       rfqtimestamp: rfqTs,
       maturitydate: matTs,


       tenorDays,
       tenor,
       tenorIndex,
       volBucket,
       side,
       status,
       convFlag,


       notional,
       spotfx:     toN(['spotfx','Spot FX']),
       fwd:        toN(['fwd','FWD']),
       fwdclient:  toN(['fwdclient','FWD-Client']),
       yieldccy:   toN(['yieldccy','Yield (CCY)']),
       yieldclient:toN(['yieldclient','Yield (Client)']),


       rejectedmessage: this.pick(row, ['rejectedmessage','Rejected message','Motivo','Reason']) || ''
     }
   },


   bucketizeTenor (days) {
     if (days >= 210) return '>=210D'
     if (days >= 180) return '180D-209D'
     if (days >= 151) return '151-179D'
     if (days >= 121) return '121-150D'
     if (days >= 91)  return '91-120D'
     if (days >= 61)  return '61-90D'
     if (days >= 31)  return '31-60D'
     return '1-30D'
   },


   bucketizeVol (notional) {
     const n = Number(notional) || 0
     if (n > 1_000_000) return '>1M'
     if (n > 500_000)   return '500k-1M'
     if (n > 100_000)   return '100k-500k'
     return '0-100k'
   },

*/
   // ===== Dimensões & Grupos =====
   buildDimensionsAndGroups () {
     // Chart 1: dimensão por índice|side (robusta)
     // Toda a aba é chaveada pelo índice das 5 bandas de vencimento
     // (flowSightBuckets), não pelo `d.tenorIndex` canônico de 8 - esse
     // continua existindo na linha normalizada e servindo o filtro global de
     // período, que filtra por dias e não por índice.
     const dimTenorIdxSide = this.ndx.dimension(d => `${hourIndexFromHour(d.hourOfDay)}|${d.side}`)

     // Every rate on this tab comes out of this one reducer - see
     // calculations/conversionRate.js. It carries {cnt, vol, volDeal}, so a
     // single group serves both the rate (volDeal/vol) and the converted
     // volume (volDeal) the bubble sizes itself by.
     const convReducer = createConversionRateReducer(
       (d) => this.analyticsProStore.convertNotionalToBRL(d) || 0
     )
     const grpConvIdx = dimTenorIdxSide.group().reduce(convReducer.add, convReducer.rem, convReducer.init)


     const dimTenorIdxSideBubble = this.ndx.dimension(d => `${hourIndexFromHour(d.hourOfDay)}|${d.side}`)
     const grpConvIdxBubble = dimTenorIdxSideBubble.group().reduce(convReducer.add, convReducer.rem, convReducer.init)
     const dimTenorIdxOnlyGroups = this.ndx.dimension(d => hourIndexFromHour(d.hourOfDay))
     // Heatmap (TENOR textual canônico × VOL)
     // Bucketed here rather than read off d.tenor / d.volBucket: those carry the
     // canonical 8-tenor label and would put the heatmap on a different ladder
     // than the axis labels it draws.
     const dimTenorVol = this.ndx.dimension(d => [
       hourLabelFromHour(d.hourOfDay),
       volumeLabelFromNotional(d.notional),
     ])


     // Same reducer, applied per side inside each cell.
     const heatReducer = {
       add: (p, v) => {
         const s = v.side
         if (!p[s]) p[s] = emptyConversionValue()
         convReducer.add(p[s], v)
         return p
       },
       rem: (p, v) => {
         const s = v.side
         if (!p[s]) p[s] = emptyConversionValue()
         convReducer.rem(p[s], v)
         return p
       },
       init: () => ({ BUY: emptyConversionValue(), SELL: emptyConversionValue() })
     }
     const grpHeat = dimTenorVol.group().reduce(heatReducer.add, heatReducer.rem, heatReducer.init)
	     //CHART 5 - table
     const dimTable = this.ndx.dimension((d,i)=>i);


     const dimSideGlobal = this.ndx.dimension(d => d.side)
     // Global counterparty filter, driven by the sidebar dropdown.
     const dimCounterparty = this.ndx.dimension(d => String(d.counterparty || '').trim())
     // RFQ-date axis, same sidebar control the Overview KPIs use. Separate from
     // dimTenorDays, which windows by maturity.
     const dimRfqDate = this.ndx.dimension(d => toDate(d.rfqtimestamp) || new Date(0))
    // Global period filter dimension shared by all Conversions charts.
     const dimTenorDays = this.ndx.dimension(d => d.tenorDays)


     this.dim = { dimTenorIdxSide, dimTenorIdxSideBubble, dimTenorIdxOnlyGroups, dimTenorVol, dimSideGlobal, dimCounterparty, dimRfqDate, dimTenorDays, dimTable }
     this.grp = { grpConvIdx, grpHeat, grpConvIdxBubble }

     this.drilldown = createDrilldownController({
       chartGroup: CHART_GROUP,
       onChange: () => this.syncFilterFlag(),
     })
       .registerSource('conversionRate', {
         toTenorIndex: (key) => Number.parseInt(String(key).split('|')[0], 10),
         dimension: dimTenorIdxSide,
         // Clicking either BUY or SELL bar selects the whole tenor bucket.
         filterPredicate: (key, selectedValues) => {
           const tenor = String(key).split('|')[0]
           return Array.from(selectedValues).some(
             (selected) => String(selected).split('|')[0] === tenor
           )
         },
       })
       .registerSource('splitVolume', {
         dimension: dimTenorIdxOnlyGroups,
       })
       .registerSource('heatmap', {
         // The cell key is [maturityLabel, volumeLabel], so the tenor index has
         // to come from the LABEL. `Number('181-360D')` is NaN, and
         // selectedTenorSets drops non-finite values - which meant a heatmap
         // selection contributed no tenor at all. Every sibling then asked
         // "is my row in the selection?", got an empty list, and painted
         // ITSELF deselected: the split chart's bar went flat grey while its
         // data was in fact filtered to that very cell.
         toTenorIndex: (cell) => {
           const idx = HOUR_LABELS.indexOf(cell?.[0])
           return idx === -1 ? NaN : idx
         },
       })
       .registerSource('bubble', {
         toTenorIndex: (key) => Number.parseInt(String(key).split('|')[0], 10),
         toDimensionKey: (key) => String(key),
         dimension: dimTenorIdxSideBubble,
       })
   },


   // ===== Chart 1 (barras) =====
   buildBarChart () {
     this.charts.bar1 = createConversionRateByHourChart({
       el: this.chartsId[0],
       chartGroup: CHART_GROUP,
       width: this.getW(this.chartsId[0]),
       height: this.getH(this.chartsId[0]),
       dims: this.dim,
       grp: this.grp,
       t: (key) => this.$t(key),
       drilldown: this.drilldown,
     })
   },


   // ===== Chart 2 (heatmap) =====
   buildHeatmap () {
     this.charts.heat2 = createConversionHeatmapChart({
       el: this.chartsId[1],
       chartGroup: CHART_GROUP,
       width: this.getW(this.chartsId[1]),
       height: this.getH(this.chartsId[1]),
       dims: this.dim,
       grp: this.grp,
       rowBuckets: this.volBuckets,
       t: (key) => this.$t(key),
       getSelectedTenorIdxs: () => this.drilldown.selectedTenorIdxs(),
       drilldown: this.drilldown,
     })
   },



   buildBarSplitChart () {
     this.charts.bar3 = createVolumeBySideChart({
       el: this.chartsId[2],
       chartGroup: CHART_GROUP,
       dims: this.dim,
       height: this.getH(this.chartsId[2]),
       uid: this._uid,
       drilldown: this.drilldown,
       store: this.analyticsProStore,
     })
   },



   buildBubbleChart () {
     this.charts.bubble4 = createConversionBubbleChart({
       el: this.chartsId[3],
       chartGroup: CHART_GROUP,
       width: this.getW(this.chartsId[3]),
       height: this.getH(this.chartsId[3]),
       dims: this.dim,
       grp: this.grp,
       t: (key) => this.$t(key),
       drilldown: this.drilldown,
     })
   },


   // ===== Wrappers p/ heatmap (ordenar e, opcional, preencher) =====
   orderedGroup (baseGroup, colsDomain, rowsDomain) {
     const colIdx = new Map(colsDomain.map((t,i) => [t, i]))
     const rowIdx = new Map(rowsDomain.map((v,i) => [v, i]))
     return {
       all: () => baseGroup.all().slice().sort((a, b) => {
         const [ta, va] = a.key || []
         const [tb, vb] = b.key || []
         const ia = colIdx.has(ta) ? colIdx.get(ta) : 9_999
         const ib = colIdx.has(tb) ? colIdx.get(tb) : 9_999
         if (ia !== ib) return ia - ib
         const ja = rowIdx.has(va) ? rowIdx.get(va) : 9_999
         const jb = rowIdx.has(vb) ? rowIdx.get(vb) : 9_999
         return ja - jb
       })
     }
   },
   // ===== Helpers de header/valor (tolerantes) =====
   pick(row, names = []) {
     for (const name of names) {
       // 1) tentativa direta
       if (row && row[name] != null && row[name] !== '') return row[name]
       // 2) variações: lower, sem espaços, sem acentos básicos
       const norm = s => String(s).normalize('NFD').replace(/[\u0300-\u036f]/g,'')
                             .replace(/\s+/g,'').toLowerCase()
       const target = norm(name)
       for (const k of Object.keys(row || {})) {
         if (norm(k) === target && row[k] != null && row[k] !== '') return row[k]
       }
     }
     return undefined
   },


   parseDateSmart(dateLike, timeLike) {
     // Aceita Date, ISO, e 'DD/MM/YYYY' + 'HH:MM:SS'
     const tryParse = (s) => {
       if (!s) return null
       if (s instanceof Date) return isNaN(s) ? null : s
       const txt = String(s).trim()
       // DD/MM/YYYY[ HH:MM(:SS)?]
       const m = txt.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/)
       if (m) {
         const [, dd, MM, yyyy, hh='00', mm='00', ss='00'] = m
         const d = new Date(`${yyyy}-${MM}-${dd}T${hh}:${mm}:${ss}Z`)
         return isNaN(d) ? null : d
       }
       const d = new Date(txt)
       return isNaN(d) ? null : d
     }


     // Se vierem separados (Date + Timestamp), combina
     if (dateLike && timeLike) {
       const d1 = String(dateLike).trim()
       const t1 = String(timeLike).trim()
       // tenta DD/MM/YYYY + HH:MM:SS
       const m = d1.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/)
       if (m) {
         const [, dd, MM, yyyy] = m
         const ts = t1.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/) ? t1 : '00:00:00'
         const d = new Date(`${yyyy}-${MM}-${dd}T${ts}Z`)
         if (!isNaN(d)) return d
       }
       // fallback: concatenado
       const d = new Date(`${d1} ${t1}`)
       if (!isNaN(d)) return d
     }


     return tryParse(dateLike) || null
   },


   toNumSmart(v) {
     if (v == null || v === '') return null
     if (typeof v === 'number') return Number.isFinite(v) ? v : null
     let s = String(v).trim()
     // Formato BR detectado quando há ',' como decimal: remove pontos de milhar e troca vírgula por ponto
     if (/[0-9]\.[0-9]{3}/.test(s) && /,/.test(s)) {
       s = s.replace(/\./g, '').replace(',', '.')
     } else if (/,/.test(s) && !/\./.test(s)) {
       s = s.replace(',', '.')
     }
     // remove qualquer caractere estranho remanescente exceto dígitos, -, .
     s = s.replace(/[^0-9\.\-]/g, '')
     const n = parseFloat(s)
     return Number.isFinite(n) ? n : null
   },


   normalizeSide(v) {
     const s = String(v || '').trim().toUpperCase()
     if (s === 'B' || s === 'BUY' || s === 'COMPRA') return 'BUY'
     if (s === 'S' || s === 'SELL' || s === 'VENDA') return 'SELL'
     return s || 'BUY'
   },


   deriveStatus(row) {
     // 1) usar 'status' se existir (dataset GPT07)
     const s = this.pick(row, ['status','Status','result','Result'])
     if (s != null && String(s).trim() !== '') return String(s).toUpperCase()
     // 2) se houver 'Side (Blotter)' preenchido, considerar como DEAL (foi executado no blotter)
     const blotterSide = this.pick(row, ['Side (Blotter)','Side(Blotter)'])
     if (blotterSide != null && String(blotterSide).trim() !== '') return 'DEAL'
     // 3) fallback neutro
     return 'RFQ'
   },
   applyGlobalSideFilter(sideOrNull) {
     const { dimSideGlobal } = this.dim || {}
     if (!dimSideGlobal) return
     if (!sideOrNull) {
       dimSideGlobal.filter(null)
     } else {
       dimSideGlobal.filterExact(sideOrNull) // 'BUY' ou 'SELL'
     }
   },
   // Empty list = no filter (see analyticsStore.counterpartyFilter). filterAll()
   // first so a narrowing selection doesn't stack on the previous one.
   _applyRfqPeriodFromStore () {
     if (!this.dim || !this.dim.dimRfqDate) return
     const { dimRfqDate } = this.dim
     const window = this.analyticsProStore.getRfqPeriodWindow
     dimRfqDate.filterAll()
     if (window) dimRfqDate.filterFunction((d) => d >= window.from && d <= window.to)
     this.scheduleRedraw()
   },
   _applyGlobalCounterpartyFromStore (names = []) {
     if (!this.dim || !this.dim.dimCounterparty) return
     const { dimCounterparty } = this.dim
     dimCounterparty.filterAll()
     // Empty means NONE now - the sidebar ticks every name by default.
     const selected = new Set(names)
     dimCounterparty.filterFunction((d) => selected.has(d))
     this.scheduleRedraw()
   },
   _applyGlobalSideFromStore (arr = []) {
     if (!this.dim || !this.dim.dimSideGlobal) {
      this._pendingDateFilter = arr
      return
     }
     const { dimSideGlobal } = this.dim

     // aplica o filtro de side como você já faz:
    if (arr.length === 1)
     {
      dimSideGlobal.filterExact(arr[0])
     }
     else
     {
       dimSideGlobal.filter(null)
     }



     // aqui trocamos o redraw direto por um agendado
     this.scheduleRedraw()
   },
    clearPageFilters() {
      this.analyticsProStore.setActiveFilter('ALL')
      this.drilldown?.clearAll({ redraw: false })
      Object.values(this.charts).forEach((chart) => {
        if (chart && chart.filterAll) chart.filterAll();
      });
      this.syncFilterFlag()
      safeRedrawAll(CHART_GROUP)
    },
    resetCurrentChart() {
      this.clearPageFilters()
    },
   _applyGlobalTenorFromStore (type) {
     if (!this.dim || !this.dim.dimTenorDays) {
       this._pendingDateFilter = type
       return
     }
     const { dimTenorDays } = this.dim
     dimTenorDays.filterAll()

     const range = this.analyticsProStore.getDateRange(
       type,
       this.analyticsProStore.customStart,
       this.analyticsProStore.customEnd
     )
     // Store returns inclusive tenor-day bounds for the selected month window.
     if (range) dimTenorDays.filterFunction((d) => d >= range[0] && d <= range[1])


     this.scheduleRedraw()
   },
   scheduleRedraw () {
     // Lazily creates the shared debounced-redraw closure once, memoized as
     // a plain (non-reactive) instance property, and reuses it thereafter.
     if (!this._scheduleRedrawFn) {
       this._scheduleRedrawFn = createScheduledRedraw(CHART_GROUP, () => this.isActive)
     }
     this._scheduleRedrawFn()
   },

   syncFilterFlag () {
     const active = this.drilldown?.hasSelections() || dc.chartRegistry.list(CHART_GROUP).some(c => c.filters?.().length)
     this.chartFilterCount = active ? 1 : 0
   }

 }
}
</script>

<style scoped>


.v-card {
  min-height: 340px;
}
/* =========================
 *  dc.js – Eixos e grid
 * ========================= */
/* Linhas e contorno dos eixos */
:deep(.dc-chart .axis path),
:deep(.dc-chart .axis line) {
  stroke: var(--muted) !important;
}
/* Texto dos eixos (X/Y). Guarda-chuva: pega TODO <text> do chart, inclusive os
   rótulos de valor dentro dos gráficos. Por isso os módulos que desenham texto
   à mão escrevem `fill:... !important` inline, e os rótulos nativos do dc
   precisam de uma regra mais específica logo abaixo. */
:deep(.dc-chart text) {
  fill: var(--muted) !important;
}

/* Gridlines internas */
:deep(.dc-chart g.grid-line line) {
  stroke: var(--muted) !important;
  opacity: 0.15;
}

/* =========================
 *  Side x Volume x Tenor (paired RowCharts)
 * ========================= */
:deep(.dc-chart g.row rect) {
  cursor: pointer;
}
:deep(.dc-chart g.row rect.deselected) {
  opacity: 0.35;
}

/* =========================
 *  Heatmap
 * ========================= */
/* Bordas (grade) entre os quadrados */
:deep(.dc-heatmap rect) {
  stroke: #000 !important;
  stroke-width: 1px !important;
  shape-rendering: crispEdges;
  width:100% !important;
  max-width: 100% !important;  
}

:deep(.dc-chart g.axis text) {
  cursor: default !important; /* ticks de eixo não parecem clicáveis */
}


::v-deep svg{
  width:100% !important;
  max-width: 100% !important;  
}


/* Marcador de legenda (BUY/SELL) */
.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}


</style>